import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import NaturalLanguageForm from '../components/NaturalLanguageForm';
import ManualFormPanel from '../components/ManualFormPanel';
import PlannedStepsTable, { stepsFromReview } from '../components/PlannedStepsTable';

export default function SearchPage() {
  const ctx = useAppContext();
  if (!ctx) return null;

  const {
    input, setInput, loading, error,
    mode, setMode,
    selectedCategory, setSelectedCategory,
    onSubmit, onInvestigateSubmit,
    isPilot, planLimits, runsToday, dailyCapReached,
    manualModels, setManualModels,
    manualElements, setManualElements,
    manualMGElementsOut, setManualMGElementsOut,
    manualMGDimensionality, setManualMGDimensionality,
    manualMGStructureHints, setManualMGStructureHints,
    manualMGPretrainedOverride, setManualMGPretrainedOverride,
    manualProperty, setManualProperty,
    manualPropertyValue, setManualPropertyValue,
    manualMGGuidance, setManualMGGuidance,
    manualMGRecordTraj, setManualMGRecordTraj,
    manualMGChunkSize, setManualMGChunkSize,
    manualNumCandidates, setManualNumCandidates,
    manualMolecularCategory, setManualMolecularCategory,
    manualSmiles, setManualSmiles,
    manualFMModel, setManualFMModel,
    manualFMReturnMode, setManualFMReturnMode,
    manualFMMaxItemsInline, setManualFMMaxItemsInline,
    manualFMChunkSize, setManualFMChunkSize,
    manualGT4SDAlgorithm, setManualGT4SDAlgorithm,
    manualGT4SDAlgorithmVersion, setManualGT4SDAlgorithmVersion,
    manualGT4SDTargetSmiles, setManualGT4SDTargetSmiles,
    manualGT4SDTargetProperty, setManualGT4SDTargetProperty,
    manualGT4SDTargetValue, setManualGT4SDTargetValue,
    manualGT4SDNumSamples, setManualGT4SDNumSamples,
    mgErrors, fmErrors, gtErrors, hasManualErrors,
    plan, review, clearReview,
    executePlannedSteps, toolResults,
  } = ctx;
  // Part 1a (2.3): the planned-steps table shows the review's rows.
  const plannedSteps = stepsFromReview(review);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={'btn btn-sm ' + (mode === 'nl' ? 'btn-success' : 'btn-outline-secondary')}
          onClick={() => setMode('nl')}
          disabled={mode === 'nl'}
        >
          Natural Language
        </button>
        <button
          className={'btn btn-sm ' + (mode === 'manual' ? 'btn-success' : 'btn-outline-secondary')}
          onClick={() => setMode('manual')}
          disabled={mode === 'manual'}
        >
          Manual
        </button>
        <button
          className={'btn btn-sm ' + (mode === 'investigate' ? 'btn-success' : 'btn-outline-secondary')}
          onClick={() => setMode('investigate')}
          disabled={mode === 'investigate'}
        >
          Investigate
        </button>
      </div>

      {isPilot && (
        <div style={{
          background: dailyCapReached ? '#f8717118' : '#f59e0b14',
          border: `1px solid ${dailyCapReached ? '#f87171' : '#f59e0b55'}`,
          borderRadius: 8, padding: '10px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 13, color: dailyCapReached ? '#f87171' : '#f59e0b' }}>
            <span style={{ fontWeight: 600 }}>Pilot</span>
            {' — '}
            {dailyCapReached
              ? `Daily run limit reached (${runsToday}/${planLimits.dailyRunCap}). Resets at midnight UTC.`
              : `${runsToday}/${planLimits.dailyRunCap} daily runs used · Max ${planLimits.maxCandidatesPerRun} candidates/run · Analyses unlimited`
            }
          </div>
          <span style={{
            background: '#f43f5e', color: '#fff', fontSize: 11, fontWeight: 700,
            padding: '4px 12px', borderRadius: 6, whiteSpace: 'nowrap', cursor: 'pointer',
            letterSpacing: 0.3,
          }}>
            Upgrade
          </span>
        </div>
      )}

      {mode === 'nl' && (
        <div style={{ marginBottom: 32 }}>
          <NaturalLanguageForm
            input={input}
            setInput={setInput}
            loading={loading}
            onSubmit={onSubmit}
            selectedCategory={selectedCategory}
            onClearCategory={() => setSelectedCategory(null)}
          />
        </div>
      )}

      {mode === 'manual' && (
        <div style={{ marginBottom: 32 }}>
        <ManualFormPanel
          manualModels={manualModels}
          setManualModels={setManualModels}
          manualElements={manualElements}
          setManualElements={setManualElements}
          manualMGElementsOut={manualMGElementsOut}
          setManualMGElementsOut={setManualMGElementsOut}
          manualMGDimensionality={manualMGDimensionality}
          setManualMGDimensionality={setManualMGDimensionality}
          manualMGStructureHints={manualMGStructureHints}
          setManualMGStructureHints={setManualMGStructureHints}
          manualMGPretrainedOverride={manualMGPretrainedOverride}
          setManualMGPretrainedOverride={setManualMGPretrainedOverride}
          manualProperty={manualProperty}
          setManualProperty={setManualProperty}
          manualPropertyValue={manualPropertyValue}
          setManualPropertyValue={setManualPropertyValue}
          manualMGGuidance={manualMGGuidance}
          setManualMGGuidance={setManualMGGuidance}
          manualMGRecordTraj={manualMGRecordTraj}
          setManualMGRecordTraj={setManualMGRecordTraj}
          manualMGChunkSize={manualMGChunkSize}
          setManualMGChunkSize={setManualMGChunkSize}
          manualNumCandidates={manualNumCandidates}
          setManualNumCandidates={setManualNumCandidates}
          manualMolecularCategory={manualMolecularCategory}
          setManualMolecularCategory={setManualMolecularCategory}
          manualSmiles={manualSmiles}
          setManualSmiles={setManualSmiles}
          manualFMModel={manualFMModel}
          setManualFMModel={setManualFMModel}
          manualFMReturnMode={manualFMReturnMode}
          setManualFMReturnMode={setManualFMReturnMode}
          manualFMMaxItemsInline={manualFMMaxItemsInline}
          setManualFMMaxItemsInline={setManualFMMaxItemsInline}
          manualFMChunkSize={manualFMChunkSize}
          setManualFMChunkSize={setManualFMChunkSize}
          manualGT4SDAlgorithm={manualGT4SDAlgorithm}
          setManualGT4SDAlgorithm={setManualGT4SDAlgorithm}
          manualGT4SDAlgorithmVersion={manualGT4SDAlgorithmVersion}
          setManualGT4SDAlgorithmVersion={setManualGT4SDAlgorithmVersion}
          manualGT4SDTargetSmiles={manualGT4SDTargetSmiles}
          setManualGT4SDTargetSmiles={setManualGT4SDTargetSmiles}
          manualGT4SDTargetProperty={manualGT4SDTargetProperty}
          setManualGT4SDTargetProperty={setManualGT4SDTargetProperty}
          manualGT4SDTargetValue={manualGT4SDTargetValue}
          setManualGT4SDTargetValue={setManualGT4SDTargetValue}
          manualGT4SDNumSamples={manualGT4SDNumSamples}
          setManualGT4SDNumSamples={setManualGT4SDNumSamples}
          mgErrors={mgErrors}
          fmErrors={fmErrors}
          gtErrors={gtErrors}
          loading={loading}
          hasManualErrors={hasManualErrors}
          onSubmit={onSubmit}
        />
        </div>
      )}

      {mode === 'investigate' && (
        <div style={{ marginBottom: 32 }}>
          <InvestigateForm onSubmit={onInvestigateSubmit} loading={loading} />
        </div>
      )}

      {error && (
        <div style={{ color: '#f87171', marginTop: 12, marginBottom: 8 }}>{error}</div>
      )}

      {plan && plannedSteps.length > 0 && !toolResults && (
        <div style={{ marginBottom: 32 }}>
          <PlannedStepsTable
            plannedSteps={plannedSteps}
            onExecute={executePlannedSteps}
            onClear={clearReview}
            onViewPlan={ctx.openPlan}
            onPreviewSelection={ctx.openSelectionPreview}
          />
        </div>
      )}

    </div>
  );
}

function InvestigateForm({ onSubmit, loading }) {
  const [formula, setFormula] = useState('');
  const [cif, setCif] = useState('');
  const [showCif, setShowCif] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formula.trim()) return;
    onSubmit({ formula: formula.trim(), cif: cif.trim() || null });
  };

  return (
    <div style={{ background: '#0f1923', borderRadius: 12, padding: 24, border: '1px solid #1e293b' }}>
      <h5 style={{ color: '#e2e8f0', marginBottom: 4, fontWeight: 600 }}>Investigate a Material</h5>
      <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 20 }}>
        Enter a chemical formula to run the full analysis pipeline. We'll automatically generate a crystal structure
        from the formula — or you can paste your own CIF for a known structure.
      </p>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>Chemical Formula *</label>
          <input
            value={formula}
            onChange={e => setFormula(e.target.value)}
            placeholder="e.g. Fe2O3, LiCoO2, BaTiO3"
            style={{
              background: '#060d18', border: '1px solid #1e3a5f', borderRadius: 6,
              color: '#e2e8f0', padding: '10px 14px', fontSize: 14, outline: 'none',
              width: '100%', maxWidth: 400,
            }}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => setShowCif(!showCif)}
            style={{
              background: 'transparent', border: 'none', color: '#38bdf8',
              fontSize: 13, cursor: 'pointer', padding: 0,
            }}
          >
            {showCif ? '▾ Hide CIF input' : '▸ Add CIF file (optional)'}
          </button>
          {showCif && (
            <textarea
              value={cif}
              onChange={e => setCif(e.target.value)}
              placeholder="Paste CIF content here to unlock structural analyses (symmetry, voronoi, DFT surrogate, 3D viewer)..."
              rows={8}
              style={{
                display: 'block', marginTop: 8, width: '100%',
                background: '#060d18', border: '1px solid #1e3a5f', borderRadius: 6,
                color: '#e2e8f0', padding: '10px 14px', fontSize: 12,
                fontFamily: 'monospace', outline: 'none', resize: 'vertical',
              }}
            />
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !formula.trim()}
          className="btn"
          style={{
            background: '#f43f5e', borderColor: '#f43f5e', color: '#fff',
            fontWeight: 600, padding: '8px 24px',
            opacity: (loading || !formula.trim()) ? 0.5 : 1,
          }}
        >
          {loading ? 'Working…' : 'Investigate'}
        </button>
      </form>
    </div>
  );
}
